import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, BarChart, Wallet, Zap, X, DollarSign } from 'lucide-react';

interface Ad {
  _id: number;
  title: string;
  clicks: number;
  ctr: number;
  budget: number;
  isActive: boolean;
}

export default function AdsList() {
  const navigate = useNavigate();
  const [ads, setAds] = useState<Ad[]>([
    {
      _id: 1,
      title: 'Summer Sale Campaign',
      clicks: 2450,
      ctr: 2.4,
      budget: 5000,
      isActive: true
    },
    {
      _id: 2,
      title: 'New Product Launch',
      clicks: 1580,
      ctr: 1.8,
      budget: 3200,
      isActive: false
    },
  ]);

  const [selectedAd, setSelectedAd] = useState<number | null>(null);
  const [budgetAmount, setBudgetAmount] = useState('');
  const [showModal, setShowModal] = useState(false);

  const handleAdClick = (adId: number) => {
    navigate(`/ads/${adId}`);
  };

  const handleBudgetUpdate = (adId: number) => {
    setSelectedAd(adId);
    setShowModal(true);
  };

  const handlePay = () => {
    if (selectedAd !== null && budgetAmount) {
      setAds(ads.map(ad => 
        ad._id === selectedAd ? { ...ad, budget: Number(budgetAmount) } : ad
      ));
      setShowModal(false);
      setBudgetAmount('');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-2xl w-96 relative">
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
            
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <DollarSign size={24} className="mr-2 text-navy-900" />
              Set Budget
            </h3>
            
            <input
              type="number"
              value={budgetAmount}
              onChange={(e) => setBudgetAmount(e.target.value)}
              placeholder="Enter amount"
              className="w-full px-4 py-2.5 border-2 border-navy-100 rounded-xl mb-4 focus:ring-2 focus:ring-navy-900"
            />
            
            <button
              onClick={handlePay}
              className="w-full py-3 bg-navy-900 text-white rounded-xl hover:bg-navy-800 transition-all"
            >
              Pay Now
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-8 flex flex-col items-center text-center space-y-4">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-navy-800 to-navy-900 bg-clip-text text-transparent">
          Advertisements
        </h1>
      </div>

      {/* Grid de Anuncios */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {ads.map((ad) => (
          <div
            key={ad._id}
            className="bg-white p-6 rounded-2xl border-2 border-navy-100 hover:shadow-xl transition-all duration-300 group relative"
          >
            <div 
              onClick={() => handleAdClick(ad._id)}
              className="cursor-pointer"
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-navy-900">{ad.title}</h3>
                <span className={`w-3 h-3 rounded-full ${ad.isActive ? 'bg-green-500' : 'bg-red-500'}`} />
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm mb-6">
                <div className="space-y-1">
                  <span className="text-navy-900 font-medium flex items-center">
                    <BarChart size={16} className="mr-2" />
                    Clicks
                  </span>
                  <p className="text-2xl font-bold text-navy-900">{ad.clicks.toLocaleString()}</p>
                </div>

                <div className="space-y-1">
                  <span className="text-navy-900 font-medium">CTR</span>
                  <p className="text-2xl font-bold text-navy-900">{ad.ctr}%</p>
                </div>

                <div className="space-y-1">
                  <span className="text-navy-900 font-medium flex items-center">
                    <Wallet size={16} className="mr-2" />
                    Budget
                  </span>
                  <p className="text-xl font-bold text-navy-900">
                    ${ad.budget.toLocaleString()}
                  </p>
                </div>

                <div className="space-y-1">
                  <span className="text-navy-900 font-medium">Status</span>
                  <p className={`text-xl font-bold ${ad.isActive ? 'text-green-600' : 'text-red-600'}`}>
                    {ad.isActive ? 'Active' : 'Paused'}
                  </p>
                </div>
              </div>
            </div>

            {/* Botones de Acción */}
            <div className="flex flex-col gap-2 border-t border-navy-100 pt-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleBudgetUpdate(ad._id);
                }}
                className="w-full py-2 bg-navy-50 text-navy-900 rounded-lg hover:bg-navy-100 transition-colors flex items-center justify-center"
              >
                <DollarSign size={16} className="mr-2" />
                Set Budget
              </button>
              
              <div 
                onClick={() => handleAdClick(ad._id)}
                className="flex items-center justify-center text-navy-900 hover:text-navy-700 cursor-pointer"
              >
                <span className="mr-2">View Details</span>
                <ArrowUpRight size={16} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}